import React, { useMemo, useState } from 'react';
import { useMoney } from "../../../hooks/useMoney";
import { Loader2 } from 'lucide-react';
import CustomModal from '../../common/CustomModal';
import {
  WalletTransaction,
  WalletTxnEventType,
  WalletTxnReconciliationStatus,
} from '../../../features/principal/types';

type SummaryGroupBy = 'day' | 'month';

interface SummaryRow {
  key: string;
  label: string;
  debitTotal: number;
  creditTotal: number;
  netAmount: number;
  count: number;
}

interface PrincipalTransactionModalProps {
  show: boolean;
  onHide: () => void;
  transactions: WalletTransaction[] | null;
  loading: boolean;
  error: string | null;
  programName: string;
}

const eventTypeLabels: Record<WalletTxnEventType, string> = {
  issuance: 'Issuance',
  revoke: 'Revoke',
  expiry: 'Expiry',
  manual_topup: 'Manual Top-up',
  other: 'Other',
};

const reconStatusBadgeClasses: Record<WalletTxnReconciliationStatus, string> = {
  not_applicable: 'bg-gray-100 text-gray-600',
  unmatched: 'bg-yellow-100 text-yellow-800',
  matched: 'bg-green-100 text-green-800',
  partially_matched: 'bg-orange-100 text-orange-800',
  manually_matched: 'bg-blue-100 text-blue-800',
  ignored: 'bg-gray-100 text-gray-600',
  disputed: 'bg-red-100 text-red-800',
};

const reconStatusLabel = (status: WalletTxnReconciliationStatus) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const PrincipalTransactionModal: React.FC<PrincipalTransactionModalProps> = ({
  show,
  onHide,
  transactions,
  loading,
  error,
  programName,
}) => {
  const { symbol, locale } = useMoney();
  const [summaryGroupBy, setSummaryGroupBy] = useState<SummaryGroupBy | null>(null);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount: string) => {
    if (!amount) return `${symbol}0`;
    return `${symbol}${parseFloat(amount).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatAmountNumber = (amount: number) =>
    `${symbol}${amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Summarised on the same rows already loaded/shown in the table below, so
  // the totals here always tie back exactly to what's visible - no separate
  // fetch, and the per-voucher view above is left untouched.
  const summaryRows: SummaryRow[] = useMemo(() => {
    if (!summaryGroupBy || !transactions || transactions.length === 0) return [];

    const groups = new Map<string, SummaryRow>();

    for (const transaction of transactions) {
      const transactionType = String(transaction.transactionTypeDisplay || '').toLowerCase();
      const isCredit = transactionType === 'credit';
      const dateString = isCredit ? transaction.createdAt : (transaction.debitDate || transaction.createdAt);
      const date = dateString ? new Date(dateString) : null;
      if (!date || Number.isNaN(date.getTime())) continue;

      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();

      const key = summaryGroupBy === 'month'
        ? `${year}-${String(month + 1).padStart(2, '0')}`
        : `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

      const label = summaryGroupBy === 'month'
        ? date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
        : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

      const amount = parseFloat(transaction.amount) || 0;

      if (!groups.has(key)) {
        groups.set(key, { key, label, debitTotal: 0, creditTotal: 0, netAmount: 0, count: 0 });
      }

      const row = groups.get(key)!;
      if (isCredit) {
        row.creditTotal += amount;
        row.netAmount += amount;
      } else {
        row.debitTotal += amount;
        row.netAmount -= amount;
      }
      row.count += 1;
    }

    return Array.from(groups.values()).sort((a, b) => (a.key < b.key ? 1 : -1));
  }, [transactions, summaryGroupBy]);

  return (
    <CustomModal
      show={show}
      onHide={onHide}
      title={`Transactions - ${programName}`}
      width="w-[1100px]"
    >
      <div className="w-full max-h-[70vh] flex flex-col">
        {loading && (
          <div className="flex justify-center items-center py-12 border border-gray-100 rounded-lg">
            <Loader2 className="w-8 h-8 animate-spin text-brand/80" />
            <span className="ml-2 text-sm text-gray-500">Loading transactions...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && transactions && transactions.length === 0 && (
          <div className="text-center py-12 text-gray-500 border border-gray-100 rounded-lg">
            No transactions found for this program.
          </div>
        )}

        {!loading && !error && transactions && transactions.length > 0 && (
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setSummaryGroupBy(null)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  summaryGroupBy === null ? 'bg-brand text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Detailed
              </button>
              <button
                type="button"
                onClick={() => setSummaryGroupBy('day')}
                className={`px-3 py-1.5 text-xs font-medium border-l border-gray-200 transition-colors ${
                  summaryGroupBy === 'day' ? 'bg-brand text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Daily Total
              </button>
              <button
                type="button"
                onClick={() => setSummaryGroupBy('month')}
                className={`px-3 py-1.5 text-xs font-medium border-l border-gray-200 transition-colors ${
                  summaryGroupBy === 'month' ? 'bg-brand text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Monthly Total
              </button>
            </div>
            <span className="text-xs text-gray-400">Based on the {transactions.length} transaction(s) loaded below</span>
          </div>
        )}

        {!loading && !error && summaryGroupBy && summaryRows.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
            <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: '260px' }}>
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2.5 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      {summaryGroupBy === 'month' ? 'Month' : 'Date'}
                    </th>
                    <th className="text-right py-2.5 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Total Debit
                    </th>
                    <th className="text-right py-2.5 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Total Credit
                    </th>
                    <th className="text-right py-2.5 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Net
                    </th>
                    <th className="text-right py-2.5 px-4 font-semibold text-gray-700">
                      Txns
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map((row, index) => (
                    <tr
                      key={row.key}
                      className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                    >
                      <td className="py-2 px-4 text-gray-700 border-r border-gray-100">{row.label}</td>
                      <td className="py-2 px-4 text-right text-red-600 border-r border-gray-100">
                        {row.debitTotal > 0 ? `-${formatAmountNumber(row.debitTotal)}` : '-'}
                      </td>
                      <td className="py-2 px-4 text-right text-green-600 border-r border-gray-100">
                        {row.creditTotal > 0 ? `+${formatAmountNumber(row.creditTotal)}` : '-'}
                      </td>
                      <td className={`py-2 px-4 text-right font-medium border-r border-gray-100 ${
                        row.netAmount >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {row.netAmount >= 0 ? '+' : '-'}{formatAmountNumber(Math.abs(row.netAmount))}
                      </td>
                      <td className="py-2 px-4 text-right text-gray-600">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !error && transactions && transactions.length > 0 && !summaryGroupBy && (
          <div className="border border-gray-200 rounded-lg overflow-hidden flex flex-col h-full min-h-0">
            <div className="overflow-x-auto overflow-y-auto flex-1" style={{ maxHeight: '600px' }}>
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Date
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Type
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Event
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Voucher
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Amount
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Bank Ref / Remark
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">
                      Recon Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction, index) => {
                    const transactionType = String(transaction.transactionTypeDisplay || '').toLowerCase();
                    const isCredit = transactionType === 'credit';
                    const date = isCredit ? transaction.createdAt : (transaction.debitDate || transaction.createdAt);
                    const eventType = transaction.eventType;
                    const reconStatus = transaction.reconciliationStatus || 'not_applicable';

                    return (
                      <tr 
                        key={transaction.transactionId} 
                        className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                        }`}
                      >
                        <td className="py-3 px-4 text-gray-700 border-r border-gray-100">
                          {formatDate(date)}
                        </td>
                        <td className="py-3 px-4 border-r border-gray-100">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              isCredit
                                ? 'bg-green-100 text-green-800 border border-green-100'
                                : 'bg-red-100 text-red-800 border border-red-100'
                            }`}
                          >
                            {transaction.transactionTypeDisplay}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-600 border-r border-gray-100">
                          {eventType ? eventTypeLabels[eventType] : '-'}
                        </td>
                        <td className="py-3 px-4 text-gray-600 border-r border-gray-100">
                          {transaction.saralId || '-'}
                        </td>
                        <td className={`py-3 px-4 text-right font-medium border-r border-gray-100 ${
                          isCredit ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {isCredit ? '+' : '-'}{formatAmount(transaction.amount)}
                        </td>
                        <td className="py-3 px-4 text-gray-600 border-r border-gray-100">
                          {transaction.remark || '-'}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              reconStatusBadgeClasses[reconStatus] || 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {reconStatusLabel(reconStatus)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {transactions && transactions.length > 0 && (
              <div className="bg-gray-50 border-t border-gray-200 px-4 py-2 text-xs text-gray-600">
                Total Transactions: {transactions.length}
              </div>
            )}
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default PrincipalTransactionModal;
